/**
 * Fleetrock mapper functions
 * Converts API models to entity models
 */

import type { API } from '../api_clients';
import type {
  FleetrockUnitMatchResult,
  FleetrockMatchResponse,
  FleetrockUnmatchedUnit,
  FleetrockSyncResult,
  FleetrockManualMatchResult,
  VMRSCodeItem,
  VMRSSystemGroup,
  VMRSCodesResponse,
} from '../../types/fleetrock';

/**
 * Maps API unit match result to entity
 */
export function mapApiFleetrockUnitMatchToEntity(
  unit: API.ResponsesFleetrockUnitMatchResult
): FleetrockUnitMatchResult {
  return {
    fleetrockId: unit.fleetrockId || '',
    unitNumber: unit.unitNumber || '',
    vin: unit.vin || '',
    year: unit.year || '',
    make: unit.make || '',
    model: unit.model || '',
    status: unit.status || '',
    matched: unit.matched || false,
    matchedBy: unit.matchedBy as FleetrockUnitMatchResult['matchedBy'],
    truckUnid: unit.truckUnid || undefined,
    truckUnitNumber: unit.truckUnitNumber || undefined,
  };
}

/**
 * Maps API match response to entity
 */
export function mapApiFleetrockMatchResponseToEntity(
  data: API.ResponsesFleetrockMatchResponseData
): FleetrockMatchResponse {
  return {
    matched: (data.matched || []).map(mapApiFleetrockUnitMatchToEntity),
    unmatched: (data.unmatched || []).map(mapApiFleetrockUnitMatchToEntity),
    total: data.total || 0,
  };
}

/**
 * Maps API unmatched unit to entity
 */
export function mapApiFleetrockUnmatchedUnitToEntity(
  unit: API.ResponsesFleetrockUnmatchedUnitItem
): FleetrockUnmatchedUnit {
  return {
    unid: unit.unid || '',
    fleetrockId: unit.fleetrockId || '',
    unitNumber: unit.unitNumber || '',
    vin: unit.vin || '',
    year: unit.year || '',
    make: unit.make || '',
    model: unit.model || '',
    status: unit.status || '',
  };
}

/**
 * Maps API sync result to entity
 */
export function mapApiFleetrockSyncResultToEntity(
  data: API.ResponsesFleetrockSyncResultData
): FleetrockSyncResult {
  return {
    created: data.created || 0,
    updated: data.updated || 0,
    skipped: data.skipped || 0,
    errors: data.errors || 0,
  };
}

/**
 * Maps API manual match response to entity
 */
export function mapApiFleetrockManualMatchToEntity(
  data: API.ResponsesFleetrockManualMatchResponseData
): FleetrockManualMatchResult {
  return {
    success: data.success || false,
    truckUnid: data.truckUnid || undefined,
  };
}

/**
 * Maps API VMRS code item to entity
 */
export function mapApiVMRSCodeItemToEntity(
  item: API.ResponsesVMRSCodeItem
): VMRSCodeItem {
  return {
    code: item.code || '',
    description: item.description || '',
  };
}

/**
 * Maps API VMRS system group to entity
 */
export function mapApiVMRSSystemGroupToEntity(
  group: API.ResponsesVMRSSystemGroup
): VMRSSystemGroup {
  return {
    code: group.code || '',
    name: group.name || '',
    codes: (group.codes || []).map(mapApiVMRSCodeItemToEntity),
  };
}

/**
 * Maps API VMRS codes response to entity
 */
export function mapApiVMRSCodesResponseToEntity(
  data: API.ResponsesVMRSCodesResponseData
): VMRSCodesResponse {
  return {
    systems: (data.systems || []).map(mapApiVMRSSystemGroupToEntity),
  };
}
